import React, { useEffect, useId, useRef, useState } from 'react';

// The white surface has real letter-shaped holes. HTML remains the accessible
// heading and the fallback until the mask has been laid out.
export default function PaintTitle({ as: Tag = 'h2', children, className = '', singleLine = false, themed = false }) {
    const ref = useRef(null);
    const id = useId().replace(/:/g, '');
    const [layout, setLayout] = useState(null);
    useEffect(() => {
        const element = ref.current;
        let disposed = false;
        let frame = null;
        let measuredWidth = null;
        const measure = () => {
            if (disposed) return;
            const width = element.clientWidth;
            if (!width) return;
            measuredWidth = width;
            if (singleLine) {
                const style = getComputedStyle(element.querySelector('.glitch') || element);
                const fitContext = document.createElement('canvas').getContext('2d');
                if (fitContext) {
                    fitContext.font = `${style.fontWeight} 42px ${style.fontFamily}`;
                    const text = style.textTransform === 'uppercase' ? String(children).toUpperCase() :
                        style.textTransform === 'lowercase' ? String(children).toLowerCase() : String(children);
                    const spacingAt42 = Math.max(0, parseFloat(style.letterSpacing) || 0) * 42 / parseFloat(style.fontSize) * text.length;
                    // Leave room for the historical offset layers on both edges.
                    const fitted = Math.min(42, 42 * (width - (themed ? 16 : 8)) / (fitContext.measureText(text).width + spacingAt42));
                    const size = `${Math.max(1, fitted).toFixed(3)}px`;
                    if (element.style.getPropertyValue('--fit-title-size') !== size) {
                        element.style.setProperty('--fit-title-size', size);
                    }
                }
            }
            let fontSize = Math.min(Tag === 'h1' ? 76 : 58, Math.max(30, width / 10));
            const context = document.createElement('canvas').getContext('2d');
            if (!context) return;
            context.font = `800 ${fontSize}px Syne, sans-serif`;
            if (singleLine) {
                fontSize *= Math.min(1, (width - 32) / context.measureText(String(children)).width);
                context.font = `800 ${fontSize}px Syne, sans-serif`;
            }
            const lines = [];
            let line = '';
            String(children).split(' ').forEach(word => {
                const next = line ? `${line} ${word}` : word;
                if (!singleLine && line && context.measureText(next).width > width - 32) { lines.push(line); line = word; }
                else line = next;
            });
            if (line) lines.push(line);
            const next = { width, fontSize, lines, height: lines.length * fontSize * 1.18 + 28 };
            setLayout(previous => previous && previous.width === next.width &&
                previous.fontSize === next.fontSize && previous.height === next.height &&
                previous.lines.join('\n') === next.lines.join('\n') ? previous : next);
        };
        // ResizeObserver runs during layout. Defer writes to the next frame,
        // and ignore the height changes caused by our own text/SVG sizing.
        const scheduleMeasure = () => {
            if (disposed || frame !== null) return;
            frame = requestAnimationFrame(() => {
                frame = null;
                measure();
            });
        };
        const observer = new ResizeObserver(() => {
            if (element.clientWidth !== measuredWidth) scheduleMeasure();
        });
        observer.observe(element);
        const themeObserver = new MutationObserver(scheduleMeasure);
        themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        scheduleMeasure();
        document.fonts?.ready.then(scheduleMeasure);
        document.fonts?.addEventListener('loadingdone', scheduleMeasure);
        return () => {
            disposed = true;
            if (frame !== null) cancelAnimationFrame(frame);
            observer.disconnect();
            themeObserver.disconnect();
            document.fonts?.removeEventListener('loadingdone', scheduleMeasure);
        };
    }, [children, Tag, singleLine, themed]);
    return <Tag className={`paint-title ${singleLine ? 'single-line-title' : ''} ${layout ? 'mask-ready' : ''} ${className}`} ref={ref} style={singleLine ? { fontSize: 'var(--fit-title-size, 1rem)' } : undefined}>
        {themed ? <span className="paint-title-text glitch-wrapper">
            <span className="glitch" data-text={children} aria-hidden="true">{children}</span>
            <span className="title-accessible-text">{children}</span>
        </span> : <span className="paint-title-text">{children}</span>}
        {layout && <svg className="paint-title-mask" aria-hidden="true" focusable="false" viewBox={`0 0 ${layout.width} ${layout.height}`}>
            <defs><mask id={id} maskUnits="userSpaceOnUse" x="0" y="0" width={layout.width} height={layout.height} style={{ maskType: 'luminance' }}>
                <rect width={layout.width} height={layout.height} fill="white" />
                {layout.lines.map((line, index) => <text key={index} x="16" y={18 + layout.fontSize * (0.88 + index * 1.18)} fill="black" style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: layout.fontSize }}>{line}</text>)}
            </mask></defs>
            <rect width={layout.width} height={layout.height} fill="white" mask={`url(#${id})`} />
        </svg>}
    </Tag>;
}
